import React, { useEffect, useState } from 'react';
import {
  Card, CardContent, Typography, Box, Button, CircularProgress, Chip
} from '@mui/material';
import { PowerSettingsNew, Wifi, Cloud } from '@mui/icons-material';
import { styled, keyframes, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

// Fun floating animation
const float = keyframes`
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
  }
  50% {
    transform: translateY(-20px) rotate(5deg);
  }
`;

const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
`;

const StyledOfflineCard = styled(Card)(({ theme }) => {
  const cardBg = theme.palette.background.paper || theme.custom?.cardBackground || 'rgba(8, 12, 24, 0.65)';
  const blur = theme.custom?.glassBlur || 20;
  const rounding = theme.shape?.borderRadius || theme.custom?.cardRounding || 28;
  
  return {
    background: `linear-gradient(135deg, ${cardBg.replace('0.65', '0.15')}, ${cardBg.replace('0.65', '0.02')})`,
    backdropFilter: `blur(${blur}px)`,
    border: '2px solid rgba(255, 68, 68, 0.3)',
    borderRadius: `${rounding}px`,
    boxShadow: `
      0 16px 64px rgba(0, 0, 0, 0.4),
      inset 0 1px 0 rgba(255, 255, 255, 0.1),
      0 0 40px rgba(255, 68, 68, 0.2)
    `,
    position: 'relative',
    overflow: 'hidden',
    minHeight: '500px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: '-50%',
      right: '-50%',
      width: '100%',
      height: '100%',
      background: 'radial-gradient(circle, rgba(255, 68, 68, 0.1), transparent)',
      animation: `${pulse} 3s ease-in-out infinite`,
    },
  };
});

const IconContainer = styled(Box)(({ theme }) => {
  const primaryColor = theme.palette.primary.main || '#00d4ff';
  return {
    position: 'relative',
    width: '120px',
    height: '120px',
    marginBottom: '30px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    animation: `${float} 3s ease-in-out infinite`,
  };
});

const PowerIcon = styled(PowerSettingsNew)(({ theme }) => {
  const primaryColor = theme.palette.primary.main || '#00d4ff';
  return {
    fontSize: '100px',
    color: '#ff4444',
    filter: 'drop-shadow(0 0 20px rgba(255, 68, 68, 0.5))',
  };
});

const WifiOffIcon = styled(Wifi)({
  position: 'absolute',
  fontSize: '50px',
  color: '#ff8888',
  animation: `${spin} 4s linear infinite`,
  opacity: 0.8,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%) rotate(-45deg)',
});

const StatusText = styled(Typography)(({ theme }) => ({
  color: '#ffffff',
  textAlign: 'center',
  marginBottom: '20px',
  textShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
  letterSpacing: '0.5px',
}));

const RefreshCountdown = styled(Box)(({ theme }) => {
  const primaryColor = theme.palette.primary.main || '#00d4ff';
  return {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '12px',
    marginTop: '30px',
    padding: '15px 20px',
    background: `${primaryColor}1a`,
    border: `1px solid ${primaryColor}4d`,
    borderRadius: '12px',
    backdropFilter: 'blur(10px)',
  };
});

const CountdownText = styled(Typography)(({ theme }) => {
  const primaryColor = theme.palette.primary.main || '#00d4ff';
  return {
    color: primaryColor,
    fontWeight: 600,
    fontSize: '0.95rem',
  };
});

const WakeButton = styled(Button)(({ theme }) => {
  const primaryColor = theme.palette.primary.main || '#00d4ff';
  const secondaryColor = theme.palette.secondary.main || '#ff4081';
  const rounding = theme.shape?.borderRadius || 12;
  
  return {
    background: `linear-gradient(135deg, ${primaryColor}26, ${primaryColor}26)`,
    border: `2px solid ${primaryColor}66`,
    borderRadius: `${rounding}px`,
    color: primaryColor,
    fontWeight: 700,
    padding: '12px 32px',
    fontSize: '1rem',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    backdropFilter: 'blur(10px)',
    position: 'relative',
    overflow: 'hidden',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    marginTop: '20px',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '-100%',
      width: '100%',
      height: '100%',
      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
      transition: 'left 0.6s',
    },
    '&:hover::before': {
      left: '100%',
    },
    '&:hover': {
      background: `linear-gradient(135deg, ${primaryColor}40, ${primaryColor}40)`,
      borderColor: `${primaryColor}cc`,
      boxShadow: `0 12px 40px ${primaryColor}4d`,
      transform: 'translateY(-3px)',
    },
    '&:disabled': {
      opacity: 0.6,
      color: `${primaryColor}80`,
    },
  };
});

const TrueNASOfflineCard = ({ onWakeOnLan, wolLoading, onRetry, apiBaseUrl, token, wolEnabled = true }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const primaryColor = theme.palette.primary.main || '#00d4ff';
  const secondaryColor = theme.palette.secondary.main || '#ff4081';
  const [autoRefreshActive, setAutoRefreshActive] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [nextWakeTime, setNextWakeTime] = useState(null);
  const [loadingWakeTime, setLoadingWakeTime] = useState(false);
  const [wakeTimeError, setWakeTimeError] = useState(null);

  // Fetch next scheduled wake from backend (derived from automation.truenas_wol triggers)
  const fetchNextWakeTime = async () => {
    if (!apiBaseUrl || !token) {
      setWakeTimeError(t('truenasOffline.unavailable'));
      setLoadingWakeTime(false);
      return;
    }
    setLoadingWakeTime(true);
    setWakeTimeError(null);
    try {
      const response = await fetch(`${apiBaseUrl}/api/homeassistant/automations/truenas_wol/next-wake`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || `API error: ${response.status}`);
      }
      const data = await response.json();
      if (data.next_wake_formatted) {
        setNextWakeTime(data.next_wake_formatted);
      } else if (data.error) {
        setWakeTimeError(t('truenasOffline.unavailable'));
        setNextWakeTime(null);
      } else {
        setWakeTimeError(t('truenasOffline.noScheduleFound'));
        setNextWakeTime(null);
      }
    } catch (error) {
      console.error('Error fetching next wake from automation:', error);
      setWakeTimeError(t('truenasOffline.unavailable'));
      setNextWakeTime(null);
    } finally {
      setLoadingWakeTime(false);
    }
  };

  // Fetch wake time on component mount
  useEffect(() => {
    fetchNextWakeTime();
    const interval = setInterval(fetchNextWakeTime, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [apiBaseUrl, token]);

  // Start auto-refresh countdown when WOL is pressed
  useEffect(() => {
    if (autoRefreshActive && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (autoRefreshActive && countdown === 0) {
      // Trigger retry when countdown reaches 0
      onRetry?.();
      setCountdown(10); // Reset for next cycle
    }
  }, [autoRefreshActive, countdown, onRetry]);

  const handleWakeOnLan = async () => {
    if (!wolEnabled) {
      return;
    }
    if (onWakeOnLan) {
      await onWakeOnLan();
      setAutoRefreshActive(true);
      setCountdown(10);
    }
  };

  return (
    <StyledOfflineCard>
      <CardContent
        sx={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          p: { xs: 3, sm: 4, md: 5 },
        }}
      >
        {/* Fun Icon Animation */}
        <IconContainer>
          <PowerIcon />
          <WifiOffIcon />
        </IconContainer>

        {/* Status Messages */}
        <StatusText variant="h3" sx={{ mb: 2, fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.8rem' } }}>
          {t('truenasOffline.title')}
        </StatusText>

        <StatusText variant="body1" sx={{ mb: 3, fontSize: { xs: '0.95rem', sm: '1.1rem' }, color: 'rgba(255, 255, 255, 0.7)' }}>
          {t('truenasOffline.description')}
        </StatusText>

        {/* Status Chip */}
        <Box sx={{ mb: 3 }}>
          <Chip
            icon={<Cloud sx={{ color: '#ff4444 !important' }} />}
            label={t('truenasOffline.statusOffline')}
            sx={{
              background: 'linear-gradient(135deg, rgba(255, 68, 68, 0.15), rgba(255, 107, 107, 0.15))',
              border: '1px solid rgba(255, 68, 68, 0.4)',
              color: '#ff4444',
              fontWeight: 600,
              fontSize: '0.95rem',
              padding: '20px 10px',
              backdropFilter: 'blur(10px)',
            }}
          />
        </Box>

        {/* Next Scheduled Wake Time from Home Assistant */}
        <Box
          sx={{
            mb: 3,
            p: 2.5,
            background: `${primaryColor}14`,
            border: `1px solid ${primaryColor}4d`,
            borderRadius: '12px',
            backdropFilter: 'blur(10px)',
            minWidth: '250px',
          }}
        >
          <Typography
            sx={{
              color: 'rgba(255, 255, 255, 0.6)',
              fontSize: '0.85rem',
              fontWeight: 500,
              marginBottom: '8px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            ⏰ {t('truenasOffline.nextScheduledWake')}
          </Typography>
          {loadingWakeTime ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} sx={{ color: primaryColor }} />
              <Typography sx={{ color: primaryColor, fontWeight: 600, fontSize: '0.95rem' }}>
                {t('truenasOffline.checking')}
              </Typography>
            </Box>
          ) : wakeTimeError ? (
            <Typography sx={{ color: '#ff8888', fontWeight: 600, fontSize: '0.95rem' }}>
              {wakeTimeError}
            </Typography>
          ) : nextWakeTime ? (
            <Typography sx={{ color: '#00ff88', fontWeight: 700, fontSize: '1.1rem' }}>
              {nextWakeTime}
            </Typography>
          ) : (
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.95rem' }}>
              {t('truenasOffline.noScheduleFound')}
            </Typography>
          )}
        </Box>

        {/* Wake On LAN Button */}
        <WakeButton
          startIcon={<PowerSettingsNew />}
          onClick={handleWakeOnLan}
          disabled={wolLoading || autoRefreshActive}
          fullWidth={false}
        >
          {wolLoading ? t('truenasOffline.sendingPacket') : autoRefreshActive ? t('truenasOffline.wakingUp') : t('truenasOffline.wakeOnLan')}
        </WakeButton>

        {/* Loading Spinner during WOL */}
        {wolLoading && (
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={40} sx={{ color: primaryColor }} />
          </Box>
        )}

        {/* Auto-refresh countdown */}
        {autoRefreshActive && countdown > 0 && (
          <RefreshCountdown>
            <CircularProgress size={24} sx={{ color: primaryColor }} />
            <CountdownText>
              {t('truenasOffline.nextCheckIn', { count: countdown })}
            </CountdownText>
          </RefreshCountdown>
        )}

        {/* Fun facts */}
        <Box
          sx={{
            mt: 4,
            p: 2,
            background: 'rgba(255, 255, 255, 0.03)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
            backdropFilter: 'blur(10px)',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: 'rgba(255, 255, 255, 0.5)',
              fontSize: '0.85rem',
              fontStyle: 'italic',
            }}
          >
            {t('truenasOffline.didYouKnow')}
          </Typography>
        </Box>
      </CardContent>
    </StyledOfflineCard>
  );
};

export default TrueNASOfflineCard;
